import type { Theme } from '@material-ui/core';

import type { FontWeight } from 'vega';
import type { Config } from 'vega-embed';

import {
  defaultChartColors,
  getChartColors,
} from '@spotify/confidence-charts-react';

const resolveFontSize = (
  fontSize: unknown,
  theme: Theme,
  fallback: number,
): number => {
  if (typeof fontSize === 'number') {
    return fontSize;
  }

  if (typeof fontSize !== 'string') {
    return fallback;
  }

  const parsedFontSize = Number.parseFloat(fontSize);
  if (!Number.isFinite(parsedFontSize)) {
    return fallback;
  }

  if (fontSize.endsWith('rem')) {
    const remPerPixel = Number.parseFloat(theme.typography.pxToRem(1));
    return Number.isFinite(remPerPixel) && remPerPixel > 0
      ? parsedFontSize / remPerPixel
      : fallback;
  }

  return parsedFontSize;
};

const encoreColor = (token: string, fallback: string): string =>
  `var(--${token}, ${fallback})`;

const numericFontWeights: FontWeight[] = [
  100, 200, 300, 400, 500, 600, 700, 800, 900,
];

const resolveFontWeight = (fontWeight: unknown): FontWeight | undefined => {
  if (typeof fontWeight === 'number') {
    return numericFontWeights.find(weight => weight === fontWeight);
  }

  if (
    fontWeight === 'normal' ||
    fontWeight === 'bold' ||
    fontWeight === 'lighter' ||
    fontWeight === 'bolder'
  ) {
    return fontWeight;
  }

  return undefined;
};

export const buildConfidenceVegaConfig = (theme: Theme): Config => {
  const isDark = theme.palette.type === 'dark';
  const chartColors = getChartColors(isDark ? 'dark' : 'light');
  const categoricalColors = defaultChartColors.map(name => chartColors[name]);
  const primaryChartColor = categoricalColors[0];
  const captionFontSize = resolveFontSize(
    theme.typography.caption.fontSize,
    theme,
    theme.typography.fontSize,
  );
  const bodyFontSize = resolveFontSize(
    theme.typography.body2.fontSize,
    theme,
    theme.typography.fontSize,
  );
  const axisTitleFontSize = resolveFontSize(
    theme.typography.body1.fontSize,
    theme,
    bodyFontSize,
  );
  const titleFontSize = resolveFontSize(
    theme.typography.h6.fontSize,
    theme,
    bodyFontSize,
  );
  const bodyFontFamily =
    theme.typography.body2.fontFamily ?? theme.typography.fontFamily;
  const titleFontFamily =
    theme.typography.h6.fontFamily ?? theme.typography.fontFamily;
  const markStrokeWidth = theme.spacing(0.25);
  const backgroundBase = encoreColor(
    'background-base',
    theme.palette.background.default,
  );
  const textBase = encoreColor('text-base', theme.palette.text.primary);
  const textSubdued = encoreColor('text-subdued', theme.palette.text.secondary);
  const essentialSubdued = encoreColor(
    'essential-subdued',
    theme.palette.text.disabled,
  );

  return {
    autosize: {
      type: 'fit-x',
      contains: 'padding',
      resize: true,
    },
    background: backgroundBase,
    padding: theme.spacing(1),
    title: {
      anchor: 'start',
      color: textBase,
      subtitleColor: textSubdued,
      font: titleFontFamily,
      subtitleFont: bodyFontFamily,
      fontSize: titleFontSize,
      subtitleFontSize: bodyFontSize,
      fontWeight: resolveFontWeight(theme.typography.fontWeightBold),
      subtitleFontWeight: resolveFontWeight(theme.typography.fontWeightRegular),
      offset: theme.spacing(1),
      subtitlePadding: theme.spacing(0.5),
    },
    axis: {
      domain: false,
      ticks: false,
      grid: false,
      labelColor: textSubdued,
      labelFont: bodyFontFamily,
      labelFontSize: captionFontSize,
      labelOverlap: 'greedy',
      labelPadding: theme.spacing(0.75),
      labelSeparation: theme.spacing(1),
      labelLimit: theme.spacing(20),
      titleColor: textBase,
      titleFont: bodyFontFamily,
      titleFontSize: axisTitleFontSize,
      titleFontWeight: resolveFontWeight(theme.typography.fontWeightMedium),
      titlePadding: theme.spacing(2),
    },
    axisX: {
      domain: true,
      domainColor: essentialSubdued,
      domainWidth: theme.spacing(0.125),
    },
    axisY: {
      domain: false,
    },
    axisDiscrete: {
      labelAngle: 0,
    },
    axisQuantitative: {
      tickCount: 6,
    },
    axisTemporal: {
      labelAngle: 0,
    },
    legend: {
      orient: 'top',
      direction: 'horizontal',
      columns: 0,
      labelColor: textSubdued,
      labelFont: bodyFontFamily,
      labelFontSize: bodyFontSize,
      labelLimit: theme.spacing(20),
      titleColor: textBase,
      titleFont: bodyFontFamily,
      titleFontSize: bodyFontSize,
      titleFontWeight: resolveFontWeight(theme.typography.fontWeightMedium),
      titlePadding: theme.spacing(0.5),
      columnPadding: theme.spacing(1.5),
      rowPadding: theme.spacing(0.5),
      padding: theme.spacing(1),
      symbolType: 'square',
      symbolSize: theme.spacing(1.5) ** 2,
    },
    range: {
      category: categoricalColors,
      ordinal: { scheme: categoricalColors },
    },
    mark: {
      color: primaryChartColor,
    },
    bar: {
      color: primaryChartColor,
    },
    line: {
      color: primaryChartColor,
      strokeWidth: markStrokeWidth,
    },
    area: {
      color: primaryChartColor,
      opacity: 0.6,
    },
    point: {
      color: primaryChartColor,
      size: 40,
    },
    text: {
      color: textSubdued,
      font: bodyFontFamily,
      fontSize: bodyFontSize,
    },
    view: {
      stroke: 'transparent',
    },
  };
};
